import { type Registry as Services, service } from '@ember/service';
import type * as Highcharts from 'highcharts';
import cloneDeep from 'lodash.clonedeep';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelAndMarketRevenueInsightBasePaceBase, {
  type HotelAndMarketRevenueInsightBasePaceBaseSignature,
} from 'lumen/components/hotel-and-market/revenue-insight/base/pace-base';
import HotelAndMarketRevenueInsightTooltipsTooltipPace from 'lumen/components/hotel-and-market/revenue-insight/tooltips/tooltip-pace';
import type { KPIS } from 'lumen/utils/constants/ri-constant-values';

interface DataPoint {
  x: number;
  y: number | null;
}

type SupportedKpi =
  | typeof KPIS.NR_OF_ROOMS
  | typeof KPIS.REVENUE
  | typeof KPIS.ADR;

export interface PaceCancellationsComponentSignature extends HotelAndMarketRevenueInsightBasePaceBaseSignature {
  Args: HotelAndMarketRevenueInsightBasePaceBaseSignature['Args'] & {
    kpi: SupportedKpi;
    onUpdateChartReference: (chart: Highcharts.Chart) => void;
    visibleKpis: SupportedKpi[];
  };
}

export default class HotelAndMarketRevenueInsightChartsChartPaceCancellations extends HotelAndMarketRevenueInsightBasePaceBase<PaceCancellationsComponentSignature> {
  @service declare riSelections: Services['ri-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const thisYearSeries = {
      name: this.riSelections.currentPeriodText,
      data: [] as DataPoint[],
      marker: { enabled: false },
      color: 'var(--prism-color-chart-status-figure-critical-normal)',
    };

    const lastYearSeries = {
      id: 'lastYear',
      name: this.riSelections.getReferencePeriodText(true),
      data: [] as DataPoint[],
      marker: { enabled: false },
      color: 'var(--prism-color-chart-neutral-figure-empty)',
    };

    const { sortedData } = this;

    const { bobKey, referenceKey } = this.riSelections;

    sortedData.forEach((dataEntry) => {
      const xValue = new Date(dataEntry.segmentationValue).getTime();

      if (dataEntry.calculatedValues[bobKey]) {
        thisYearSeries.data.push({
          x: xValue,
          y: dataEntry.calculatedValues[bobKey][this.args.kpi] ?? null,
        });
      }

      if (dataEntry.calculatedValues[referenceKey]) {
        lastYearSeries.data.push({
          x: xValue,
          y: dataEntry.calculatedValues[referenceKey][this.args.kpi] ?? null,
        });
      }
    });

    return [thisYearSeries, lastYearSeries];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    const chartOptions = cloneDeep(this.baseChartOptions);

    if (this.riSelections.isCancellationsPercentageView) {
      chartOptions.yAxis = {
        plotLines: [
          {
            value: 0,
            width: 1,
          },
        ],
        title: {
          text: null,
        },
        max: 100,
        min: 0,
        labels: {
          formatter(): string {
            return `${this.value}%`;
          },
        },
      };
    }

    chartOptions.chart.height = '247px'; // The cancellations pace chart uses highcharts legend instead of our own so we need to fill more height

    return chartOptions;
  }

  <template>
    <GeneralChartsOtaChart
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />
    <div class="hidden" id="dummy-div-for-tooltip-pace-chart">
      <div class="revenue-insight-segmentation-value-tooltip">
        {{! added if here since this tooltip has some instensive calculations and I wanted to avoid doing them if there was no data}}
        {{#if this.tooltipData}}
          <HotelAndMarketRevenueInsightTooltipsTooltipPace
            @data={{this.tooltipData}}
            @kpis={{@visibleKpis}}
          />
        {{/if}}
      </div>
    </div>
  </template>
}
